import { useEffect, useState } from "react";
import { useMemo } from "react";
import PropTypes from "prop-types";
import { Form, Select } from "antd";
import { ContentApi } from "../../../../shared/api/contentApi";

const ENTITY_SEARCH_DEBOUNCE_MS = 450;

export default function AutoApplyEntitiesField({
    open,
    projectId,
    versionId,
    selectedSystemNames,
    selectedItems,
    onChange,
    fieldName = "autoApplyForEntityTypes",
    label = "Автоприменение по systemName",
    placeholder = "Поиск сущностей по systemName...",
}) {
    const [entityQuery, setEntityQuery] = useState("");
    const [debouncedEntityQuery, setDebouncedEntityQuery] = useState("");
    const [entityOptions, setEntityOptions] = useState([]);
    const [loadingEntities, setLoadingEntities] = useState(false);
    const [entitiesError, setEntitiesError] = useState("");

    useEffect(() => {
        if (!open) return;
        setEntityQuery("");
        setDebouncedEntityQuery("");
        setEntityOptions([]);
        setEntitiesError("");
        setLoadingEntities(false);
    }, [open]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedEntityQuery(entityQuery);
        }, ENTITY_SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [entityQuery]);

    useEffect(() => {
        if (!open) return;

        const query = debouncedEntityQuery.trim();
        if (!query || !projectId || !versionId) {
            setEntityOptions([]);
            setLoadingEntities(false);
            setEntitiesError("");
            return;
        }

        const ac = new AbortController();
        setLoadingEntities(true);
        setEntitiesError("");

        ContentApi.getEntities(
            {
                projectId,
                versionId,
            },
            {
                signal: ac.signal,
                params: {
                    page: 1,
                    pageSize: 20,
                    "q.systemName:contains": query,
                },
            },
        )
            .then((data) => {
                if (ac.signal.aborted) return;
                setEntityOptions(Array.isArray(data?.items) ? data.items : []);
            })
            .catch((error) => {
                if (ac.signal.aborted) return;
                setEntitiesError(error?.message || "Не удалось загрузить сущности");
                setEntityOptions([]);
            })
            .finally(() => {
                if (!ac.signal.aborted) setLoadingEntities(false);
            });

        return () => ac.abort();
    }, [open, projectId, versionId, debouncedEntityQuery]);

    const selected = Array.isArray(selectedSystemNames)
        ? selectedSystemNames
        : Array.isArray(selectedItems)
            ? selectedItems
            : [];

    const selectOptions = useMemo(() => {
        const map = new Map();

        for (const entity of entityOptions) {
            const systemName = entity?.systemName;
            if (!systemName) continue;
            map.set(systemName, {
                label: `${entity?.name} (${systemName})`,
                value: systemName,
            });
        }

        for (const item of selected) {
            if (!map.has(item)) {
                map.set(item, {
                    label: item,
                    value: item,
                });
            }
        }

        return Array.from(map.values());
    }, [entityOptions, selected]);

    const notFoundContent = useMemo(() => {
        if (!entityQuery.trim()) return "Начните вводить systemName для поиска сущностей";
        if (loadingEntities) return "Загрузка...";
        if (entitiesError) return entitiesError;
        return "По вашему запросу сущности не найдены";
    }, [entityQuery, loadingEntities, entitiesError]);

    return (
        <Form.Item label={label} className="flex flex-col mb-1">
            <Select
                mode="tags"
                size="medium"
                className="mt-2 w-full"
                placeholder={placeholder}
                value={selected}
                onChange={(nextSelected) => onChange(nextSelected)}
                onSearch={setEntityQuery}
                options={selectOptions}
                filterOption={false}
                notFoundContent={notFoundContent}
            />
        </Form.Item>
    );
    
}

AutoApplyEntitiesField.propTypes = {
    open: PropTypes.bool,
    projectId: PropTypes.string,
    versionId: PropTypes.string,
    selectedSystemNames: PropTypes.arrayOf(PropTypes.string),
    selectedItems: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    fieldName: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
};
